import { normalizeUSPhone } from '@/store/modules/salesforce'

export type InputFormat = 'email' | 'phone' | 'order' | 'proofId' | 'unknown'

export type OrderSystem = 'avetti' | 'mercury'

// ─── Order number detection ───────────────────────────────────────────────────

// Mercury suffix charset: digits + A-Z excluding I, L, O (ambiguous with 1, l, 0)
// Must contain at least one letter (otherwise it's Avetti)
const RE_MERCURY_SUFFIX = /^[0-9A-HJ-KM-NP-Z]{5}$/
const RE_MERCURY_PARTIAL = /^[0-9A-HJ-KM-NP-Za-hj-km-np-z]+$/

/**
 * Validate a YYMMDD date prefix. Year must be 2011+ and <= current year,
 * month 1-12, day 1-maxDay for that month.
 */
function isValidOrderDate(yy: number, mm: number, dd: number): boolean {
  const currentYY = new Date().getFullYear() % 100
  if (yy < 11 || yy > currentYY || mm < 1 || mm > 12 || dd < 1) return false
  const maxDay = new Date(2000 + yy, mm, 0).getDate()
  return dd <= maxDay
}

/**
 * Check if a string could be a Paper Culture order number.
 *
 * Avetti format: YYMMDD##### (11 digits, or 6+ partial while typing)
 * Mercury format: YYMMDDXXXXX (6 date digits + 5 alphanumeric suffix)
 *
 * Formatting chars (spaces, dashes, parens) are stripped before checking.
 */
export function couldBeOrderNumber(val: string): boolean {
  return normalizeOrderNumber(val) !== null
}

/**
 * Normalize an order number input, returning the canonical form and system.
 * Returns null if the input is not a valid order number.
 */
export function normalizeOrderNumber(
  val: string
): { system: OrderSystem; value: string } | null {
  // Strip formatting chars
  const cleaned = val.replace(/[\s\-()]/g, '')
  if (cleaned.length < 6 || cleaned.length > 11) return null

  // First 6 chars must be digits forming a valid date
  const dateStr = cleaned.slice(0, 6)
  if (!/^\d{6}$/.test(dateStr)) return null
  const yy = parseInt(dateStr.slice(0, 2))
  const mm = parseInt(dateStr.slice(2, 4))
  const dd = parseInt(dateStr.slice(4, 6))
  if (!isValidOrderDate(yy, mm, dd)) return null

  const suffix = cleaned.slice(6)

  // Partial order (date only, still typing) — assume Avetti
  if (!suffix) return { system: 'avetti', value: dateStr }

  // Avetti: suffix is all digits (partial or full, up to 5)
  if (/^\d+$/.test(suffix) && suffix.length <= 5) {
    return { system: 'avetti', value: cleaned }
  }

  // Mercury: 5-char alphanumeric suffix (uppercase, no I/L/O).
  // Must contain at least one letter (all-digit was caught by Avetti above).
  const upperSuffix = suffix.toUpperCase()
  if (
    suffix.length === 5 &&
    RE_MERCURY_SUFFIX.test(upperSuffix) &&
    /[A-Z]/.test(upperSuffix)
  ) {
    return { system: 'mercury', value: dateStr + upperSuffix }
  }

  // Partial Mercury: < 5 chars matching Mercury charset
  if (
    suffix.length < 5 &&
    RE_MERCURY_PARTIAL.test(suffix) &&
    /[A-Za-z]/.test(suffix)
  ) {
    return { system: 'mercury', value: dateStr + upperSuffix }
  }

  return null
}

// ─── Proof ID detection & normalization ───────────────────────────────────────

const B64URL =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'
const CB32 = '0123456789abcdefghjkmnpqrstvwxyz'

const RE_B64URL_22 = /^[A-Za-z0-9_-]{22}$/
const RE_CB32_26 = /^[0-9a-hjkmnp-tv-z]{26}$/i
const RE_DSN_PREFIX = /^dsn_(.+)$/i

/**
 * Convert a 26-char Crockford Base32 string to 16 bytes (UUID).
 * Returns null if the input contains invalid CB32 characters.
 */
function cb32ToBytes(s: string): Uint8Array | null {
  const v: number[] = []
  for (const c of s.toLowerCase()) {
    const i = CB32.indexOf(c)
    if (i < 0) return null
    v.push(i)
  }
  if (v.length !== 26) return null

  // 26 x 5 bits = 130 bits. Top 2 bits of v[0] must be 0 (padding).
  // Remaining 128 bits pack big-endian into 16 bytes.
  const b = new Uint8Array(16)
  b[0] = ((v[0] & 0x07) << 5) | v[1]
  b[1] = (v[2] << 3) | (v[3] >> 2)
  b[2] = ((v[3] & 0x03) << 6) | (v[4] << 1) | (v[5] >> 4)
  b[3] = ((v[5] & 0x0f) << 4) | (v[6] >> 1)
  b[4] = ((v[6] & 0x01) << 7) | (v[7] << 2) | (v[8] >> 3)
  b[5] = ((v[8] & 0x07) << 5) | v[9]
  b[6] = (v[10] << 3) | (v[11] >> 2)
  b[7] = ((v[11] & 0x03) << 6) | (v[12] << 1) | (v[13] >> 4)
  b[8] = ((v[13] & 0x0f) << 4) | (v[14] >> 1)
  b[9] = ((v[14] & 0x01) << 7) | (v[15] << 2) | (v[16] >> 3)
  b[10] = ((v[16] & 0x07) << 5) | v[17]
  b[11] = (v[18] << 3) | (v[19] >> 2)
  b[12] = ((v[19] & 0x03) << 6) | (v[20] << 1) | (v[21] >> 4)
  b[13] = ((v[21] & 0x0f) << 4) | (v[22] >> 1)
  b[14] = ((v[22] & 0x01) << 7) | (v[23] << 2) | (v[24] >> 3)
  b[15] = ((v[24] & 0x07) << 5) | v[25]
  return b
}

/** Convert 16 bytes to a 22-char Base64url string (no padding). */
function bytesToB64url(b: Uint8Array): string {
  let out = ''
  for (let i = 0; i < 16; i += 3) {
    const b0 = b[i]
    const b1 = b[i + 1] ?? 0
    const b2 = b[i + 2] ?? 0
    out += B64URL[b0 >> 2]
    out += B64URL[((b0 & 0x03) << 4) | (b1 >> 4)]
    if (i + 1 < 16) out += B64URL[((b1 & 0x0f) << 2) | (b2 >> 6)]
    if (i + 2 < 16) out += B64URL[b2 & 0x3f]
  }
  return out
}

/** Convert a 26-char Crockford Base32 string to 22-char Base64url. */
export function cb32ToB64url(cb32: string): string | null {
  const bytes = cb32ToBytes(cb32)
  if (!bytes) return null
  return bytesToB64url(bytes)
}

/**
 * Normalize a proof ID to the 22-char Base64url form used in Salesforce.
 *
 * Accepts:
 * - 22-char Base64url: returned as-is
 * - 26-char Crockford Base32: converted to Base64url
 * - dsn_ + 26-char CB32: prefix stripped, converted to Base64url
 * - 3+ char prefix: returned as-is (for LIKE prefix search)
 *
 * Returns null for inputs too short (< 3 chars).
 */
export function normalizeProofId(input: string): string | null {
  if (!input || input.length < 3) return null

  // dsn_ prefix: strip and convert CB32 body
  const dsnMatch = RE_DSN_PREFIX.exec(input)
  if (dsnMatch) {
    const body = dsnMatch[1]
    if (RE_CB32_26.test(body)) {
      return cb32ToB64url(body.toLowerCase())
    }
    // dsn_ with wrong-length body — return body as prefix for LIKE search
    return body
  }

  // 26-char Crockford Base32 — convert to Base64url
  if (RE_CB32_26.test(input)) {
    return cb32ToB64url(input.toLowerCase()) ?? input
  }

  // 22-char Base64url — already canonical
  if (RE_B64URL_22.test(input)) {
    return input
  }

  // Short prefix for LIKE search (3+ chars)
  return input
}

/**
 * Check if an input looks like a full or partial proof ID.
 * - 22-char Base64url (exact match)
 * - 26-char Crockford Base32 (exact match)
 * - dsn_ prefix (TypeID)
 * - 3+ chars matching proof ID charset (letters, digits, hyphens, underscores)
 *
 * All-digit strings are accepted: proof IDs can start with digits, and
 * detectFormat already tries order/phone first, so reaching this function
 * means those didn't match.
 */
export function couldBeProofId(val: string): boolean {
  if (!val || val.length < 3) return false

  // dsn_ prefix always means proof
  if (RE_DSN_PREFIX.test(val)) return true

  // Full 22-char Base64url
  if (RE_B64URL_22.test(val)) return true

  // Full 26-char CB32
  if (RE_CB32_26.test(val)) return true

  // Proof ID charset: letters, digits, hyphens, underscores.
  // Must contain at least one alphanumeric character.
  if (/^[A-Za-z0-9_-]+$/.test(val) && /[A-Za-z0-9]/.test(val)) {
    return true
  }

  return false
}

// ─── Formatting helpers ───────────────────────────────────────────────────────

/** Format 10 US phone digits as (XXX) XXX-XXXX */
export function formatPhone(digits: string): string {
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

/** Format order number with space after date part: YYMMDD XXXXX */
export function formatOrder(val: string): string {
  const cleaned = val.replace(/[\s\-()]/g, '')
  return cleaned.length >= 7
    ? `${cleaned.slice(0, 6)} ${cleaned.slice(6)}`
    : cleaned
}

// ─── Main detector ────────────────────────────────────────────────────────────

/**
 * Detect the input format for the customer search box.
 *
 * Priority: email > order > phone > proofId.
 * Pure-digit strings are never classified as proofId.
 *
 * Order numbers: Avetti (YYMMDD#####) or Mercury (YYMMDDXXXXX).
 * Phone numbers: US 10-digit (or 11 with leading 1).
 * Proof IDs: Base64url (22 chars), Crockford Base32 (26 chars), dsn_ TypeID,
 *            or alphanumeric prefix (3+ chars with non-digit characters).
 */
export function detectFormat(val: string): InputFormat {
  if (!val) return 'unknown'
  if (val.includes('@')) return 'email'

  if (couldBeOrderNumber(val)) return 'order'

  // US phone: 10 digits, or 11 starting with 1
  if (normalizeUSPhone(val)) return 'phone'

  // Pure digits that aren't order or phone — could still be a proof ID prefix
  if (couldBeProofId(val)) return 'proofId'

  return 'unknown'
}
